/**
 * Cookie consent, the browser half. `bootConsent()` runs once from
 * entry.client.tsx before hydration: it reads `tt_consent`, loads the
 * analytics vendors for a returning visitor who allowed them, opens the
 * preferences dialog for any `[data-tt-consent-open]` click (the footer
 * button and the Cookie Policy's static HTML) and pauses session recording
 * on identity, bank and payment screens.
 *
 * The banner and dialog (app/components/consent/CookieConsent.tsx) read this
 * store through useSyncExternalStore. Google Consent Mode's defaults are set
 * earlier, by the inline head script the root renders from the same cookie
 * (consentModeBootScript), so GTM never fires unconsented.
 */
import {
	ANALYTICS_COOKIES,
	ANALYTICS_STORAGE_KEYS,
	ANALYTICS_VENDORS,
	type ConsentChoice,
	type ConsentMethod,
	type ConsentRecord,
	NO_RECORD_SELECTOR,
	PREFERENCE_COOKIES,
	choiceFor,
	consentModeState,
	expireCookieStrings,
	getConsentFromCookieHeader,
	isNoRecordPath,
	makeConsentRecord,
	matchesAny,
	serializeConsentCookie,
} from './consent'

type Gtag = (...args: unknown[]) => void
type Clarity = ((...args: unknown[]) => void) & { q?: unknown[][] }
declare global {
	interface Window {
		dataLayer?: unknown[]
		gtag?: Gtag
		clarity?: Clarity
	}
}

const state = {
	booted: false,
	record: null as ConsentRecord | null,
	dialogOpen: false,
	/** Set by the last save, for the banner's polite status line. */
	saved: false,
	lastFocus: null as HTMLElement | null,
	clarityLoaded: false,
	recordingAllowed: false,
}
const listeners = new Set<() => void>()
const recordingListeners = new Set<(allowed: boolean) => void>()

function emit() {
	for (const fn of listeners) fn()
}

export function subscribeConsent(fn: () => void) {
	listeners.add(fn)
	return () => {
		listeners.delete(fn)
	}
}
export const getConsentRecord = () => state.record
export const hasConsentRecord = () => state.record !== null
export const isPreferencesOpen = () => state.dialogOpen
export const wasConsentSaved = () => state.saved

/**
 * Session replay tools (Sentry Replay, from monitoring.client.tsx) follow
 * this: called now with the current answer, then on every change.
 */
export function subscribeRecording(fn: (allowed: boolean) => void) {
	recordingListeners.add(fn)
	fn(state.recordingAllowed)
	return () => {
		recordingListeners.delete(fn)
	}
}

export function bootConsent() {
	if (state.booted) return
	state.booted = true
	state.record = getConsentFromCookieHeader(document.cookie)
	if (state.record) apply(state.record, null)
	document.addEventListener('click', onOpenerClick)
	watchForSensitiveScreens()
}

export function openPreferences() {
	if (!state.dialogOpen && document.activeElement instanceof HTMLElement) {
		state.lastFocus = document.activeElement
	}
	state.dialogOpen = true
	emit()
}

export function closePreferences() {
	if (!state.dialogOpen) return
	state.dialogOpen = false
	emit()
}

/** Where focus goes back to when the dialog closes, if it is still there. */
export function takeLastFocus() {
	const el = state.lastFocus
	state.lastFocus = null
	return el?.isConnected ? el : null
}

export function chooseConsent(method: ConsentMethod, custom?: ConsentChoice) {
	const previous = state.record
	const record = makeConsentRecord({
		method,
		choice: choiceFor(method, custom),
		previous,
		now: new Date(),
		newId: randomId(),
	})
	document.cookie = serializeConsentCookie(record, {
		hostname: window.location.hostname,
		secure: window.location.protocol === 'https:',
	})
	state.record = record
	state.dialogOpen = false
	state.saved = true
	apply(record, previous)
	emit()
}

function onOpenerClick(event: MouseEvent) {
	const target =
		event.target instanceof Element
			? event.target.closest(
					'[data-tt-consent-open], a[href="#cookie-settings"]',
				)
			: null
	if (!target) return
	event.preventDefault()
	openPreferences()
}

function randomId() {
	const bytes = new Uint8Array(12)
	crypto.getRandomValues(bytes)
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function gtag(...args: unknown[]) {
	window.dataLayer = window.dataLayer || []
	if (typeof window.gtag === 'function') window.gtag(...args)
	else window.dataLayer.push(args)
}

function apply(record: ConsentRecord, previous: ConsentRecord | null) {
	const c = record.categories
	gtag('consent', 'update', consentModeState(c))
	window.dataLayer?.push({
		event: 'tt_consent_update',
		tt_consent_analytics: c.analytics,
		tt_consent_preferences: c.preferences,
		tt_consent_version: record.version,
	})
	if (c.analytics) loadAnalytics()
	else if (previous?.categories.analytics) stopAnalytics()
	if (!c.preferences && previous?.categories.preferences) {
		deleteCookies(PREFERENCE_COOKIES)
	}
	if (!c.analytics) deleteCookies(ANALYTICS_COOKIES)
	checkRecording()
}

function addScript(
	id: string,
	src: string,
	attrs: Record<string, string> = {},
) {
	if (document.getElementById(id)) return
	const s = document.createElement('script')
	s.id = id
	s.async = true
	s.src = src
	for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v)
	document.head.appendChild(s)
}

function loadAnalytics() {
	const { clarityProjectId, ahrefsKey } = ANALYTICS_VENDORS
	// Microsoft Clarity. Masking must be Strict in the Clarity dashboard.
	if (!state.clarityLoaded) {
		state.clarityLoaded = true
		// Clarity's own stub: calls queue until the tag loads and replays them.
		const queue: unknown[][] = []
		window.clarity =
			window.clarity ||
			Object.assign(
				(...args: unknown[]) => {
					queue.push(args)
				},
				{ q: queue },
			)
		addScript(
			'tt-clarity',
			`https://www.clarity.ms/tag/${encodeURIComponent(clarityProjectId)}`,
		)
	}
	window.clarity?.('consentv2', {
		ad_Storage: 'denied',
		analytics_Storage: 'granted',
	})
	// Ahrefs Web Analytics sets no cookies.
	addScript('tt-ahrefs', 'https://analytics.ahrefs.com/analytics.js', {
		'data-key': ahrefsKey,
	})
}

function stopAnalytics() {
	try {
		window.clarity?.('consentv2', {
			ad_Storage: 'denied',
			analytics_Storage: 'denied',
		})
		// Clarity erases its cookies and ends the session.
		window.clarity?.('consent', false)
		window.clarity?.('stop')
	} catch {
		// Clarity not loaded yet
	}
	// Ahrefs keeps nothing; removing the tag stops it from the next page on.
	document.getElementById('tt-ahrefs')?.remove()
	deleteCookies(ANALYTICS_COOKIES)
	clearStorage(ANALYTICS_STORAGE_KEYS)
}

function deleteCookies(patterns: readonly (string | RegExp)[]) {
	const names = document.cookie
		? document.cookie.split('; ').map((p) => p.split('=')[0] ?? '')
		: []
	for (const name of names) {
		if (!matchesAny(name, patterns)) continue
		for (const c of expireCookieStrings(name, window.location.hostname)) {
			document.cookie = c
		}
	}
}

function clearStorage(patterns: readonly (string | RegExp)[]) {
	for (const area of ['sessionStorage', 'localStorage'] as const) {
		try {
			const store = window[area]
			const keys = Array.from({ length: store.length }, (_, i) => store.key(i))
			for (const k of keys)
				if (k && matchesAny(k, patterns)) store.removeItem(k)
		} catch {
			// storage blocked
		}
	}
}

function onSensitiveScreen() {
	return (
		isNoRecordPath(window.location.pathname) ||
		document.querySelector(NO_RECORD_SELECTOR) !== null
	)
}

/** Recording runs only with analytics consent and off sensitive screens. */
function checkRecording() {
	const allowed =
		Boolean(state.record?.categories.analytics) && !onSensitiveScreen()
	if (allowed === state.recordingAllowed) return
	state.recordingAllowed = allowed
	try {
		if (state.clarityLoaded) window.clarity?.(allowed ? 'start' : 'stop')
	} catch {
		// ignore
	}
	for (const fn of recordingListeners) fn(allowed)
}

/**
 * Client navigations and the screening wizard's steps both change the DOM,
 * so one batched observer catches every way onto or off a sensitive screen.
 */
function watchForSensitiveScreens() {
	checkRecording()
	let pending = false
	new MutationObserver(() => {
		if (pending) return
		pending = true
		setTimeout(() => {
			pending = false
			checkRecording()
		}, 50)
	}).observe(document.documentElement, { childList: true, subtree: true })
	window.addEventListener('popstate', checkRecording)
}
